import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { moneyCompact, num } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList, Cell,
} from "recharts";
import { useChartTheme } from "@/lib/chartTheme";
import { CostDonut } from "@/components/charts/ChartKit";
import { StatCard } from "@/components/ui/StatCard";
import { Trophy, TrendingUp, Layers, Percent, Search, Download, X } from "lucide-react";

const LOSS_COLORS = ["#F43F5E", "#FB7185", "#F59E0B", "#FBBF24", "#6366F1", "#818CF8", "#0EA5E9", "#38BDF8", "#94A3B8"];
const WIN_COLORS = ["#10B981", "#34D399", "#059669", "#6EE7B7", "#0D9488", "#2DD4BF", "#14B8A6", "#5EEAD4", "#94A3B8"];

export default function Analytics() {
  const [a, setA] = useState(null);
  const [search, setSearch] = useState("");
  const [outcome, setOutcome] = useState("all");
  const [client, setClient] = useState("all");
  const [state, setState] = useState("all");
  const [reasonFilter, setReasonFilter] = useState(null);
  const [openBucket, setOpenBucket] = useState(null);
  const tableRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/analytics").then((r) => setA(r.data)).catch(() => {});
  }, []);

  const ct = useChartTheme();
  const records = useMemo(() => a?.records || [], [a]);

  const clients = useMemo(() => [...new Set(records.map((r) => r.client).filter(Boolean))].sort(), [records]);
  const states = useMemo(() => [...new Set(records.map((r) => r.state).filter(Boolean))].sort(), [records]);

  const reasonOf = (r, oc) => (oc === "won" ? ((r.win_reason || "").trim() || "Unspecified") : (r.loss_bucket || "Other"));
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return records.filter((r) =>
      (outcome === "all" || r.outcome === outcome) &&
      (client === "all" || r.client === client) &&
      (state === "all" || r.state === state) &&
      (reasonFilter == null || (r.outcome === reasonFilter.outcome && reasonOf(r, reasonFilter.outcome) === reasonFilter.reason)) &&
      (!s || [r.name, r.client, r.state, r.loss_reason, r.win_reason].some((v) => (v || "").toLowerCase().includes(s))),
    );
  }, [records, search, outcome, client, state, reasonFilter]);

  const anyFilter = search || outcome !== "all" || client !== "all" || state !== "all" || reasonFilter;
  const clearFilters = () => { setSearch(""); setOutcome("all"); setClient("all"); setState("all"); setReasonFilter(null); };

  // Drill down from a reason chart into just those won / lost bids.
  const pickReason = (bidOutcome, reason) => {
    if (!reason) return;
    setReasonFilter({ outcome: bidOutcome, reason });
    setOutcome(bidOutcome);
    setTimeout(() => tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const exportCsv = () => {
    const head = ["Project", "Client", "State", "MW", "$/W", "Total", "Outcome", "Loss reason"];
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [head.join(",")].concat(
      filtered.map((r) => [r.name, r.client || "", r.state || "", r.mw ?? "",
        r.ppw != null ? Number(r.ppw).toFixed(4) : "", r.total ?? "", r.outcome || "", r.loss_reason || ""].map(esc).join(",")),
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `bid_history_${new Date().toISOString().slice(0, 10)}.csv`; link.click();
    URL.revokeObjectURL(url);
  };

  if (!a) return <div className="p-8 text-sm text-slate-400">Loading history…</div>;

  const lostReasons = a.lost_reasons || [];
  const winReasons = a.win_reasons || [];
  const lostTrend = a.lost_trend || [];
  const reasonKeys = a.lost_reason_keys || [];
  const wonTrend = a.won_trend || [];

  return (
    <div className="max-w-[1800px] w-full mx-auto px-6 sm:px-8 py-8 space-y-8">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Bid history</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          {num(a.total_count)} decided pursuits, imported as stored records. Every bid in one consistent shape.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="analytics-kpi-0" icon={Percent} accent="#10B981" label="Hit rate — count" value={`${a.hit_rate_count}%`} sub={`${a.won_count} of ${a.total_count} won`} />
        <StatCard testid="analytics-kpi-1" icon={TrendingUp} accent="#003E87" label="Hit rate — value" value={`${a.hit_rate_value}%`} sub={`${moneyCompact(a.won_value)} of ${moneyCompact(a.total_value)}`} />
        <StatCard testid="analytics-kpi-2" icon={Trophy} accent="#F4C400" label="Won value" value={moneyCompact(a.won_value)} sub="lifetime" />
        <StatCard testid="analytics-kpi-3" icon={Layers} accent="#6366F1" label="Total bid" value={moneyCompact(a.total_value)} sub="decided pursuits" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5" data-testid="analytics-chart-cost-per-mw">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 mb-4">Cost per MW by state</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={a.cost_per_mw} margin={{ left: 10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={ct.grid} vertical={false} />
              <XAxis dataKey="state" tick={{ fontSize: 11, fill: ct.tick }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => moneyCompact(v)} tick={{ fontSize: 11, fill: ct.tick }} axisLine={false} tickLine={false} width={52} />
              <Tooltip formatter={(v) => moneyCompact(v)} contentStyle={ct.contentStyle} itemStyle={ct.itemStyle} labelStyle={ct.labelStyle} cursor={{ fill: ct.cursor }} />
              <Bar dataKey="cost_per_mw" fill={ct.bar} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5" data-testid="analytics-chart-won-lost">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 mb-4">Won vs lost — value</h2>
          <CostDonut height={200}
            data={[
              { label: "Won", value: a.won_value || 0, color: "#10B981" },
              { label: "Lost / open", value: Math.max(0, (a.total_value || 0) - (a.won_value || 0)), color: "#94A3B8" },
            ]}
            centerLabel="Total bid" centerValue={moneyCompact(a.total_value || 0)} valueFmt={moneyCompact} />
        </Card>
      </div>

      {/* Why we lose — loss-reason breakdown */}
      <Card className="p-5" data-testid="analytics-lost-reasons">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">Why we lose — lost bids by reason</h2>
          <span className="text-xs text-slate-400">{num(a.lost_count || 0)} lost pursuit{(a.lost_count || 0) === 1 ? "" : "s"}</span>
        </div>
        {lostReasons.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-400" data-testid="analytics-lost-empty">No lost bids on record yet. As pursuits are marked lost with a reason, they'll break out here.</div>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(180, lostReasons.length * 46)} minHeight={180}>
            <BarChart data={lostReasons} layout="vertical" margin={{ left: 10, right: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={ct.grid} horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: ct.tick }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="reason" width={168} interval={0} tick={{ fontSize: 11, fill: ct.tick }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v, n, p) => [`${v} bid${v === 1 ? "" : "s"} · ${moneyCompact(p.payload.value)}`, "Lost"]}
                contentStyle={ct.contentStyle} itemStyle={ct.itemStyle} labelStyle={ct.labelStyle} cursor={{ fill: ct.cursor }} />
              <Bar dataKey="count" radius={[0, 4, 4, 0]} className="cursor-pointer" onClick={(d) => pickReason("lost", d?.reason || d?.payload?.reason)}>
                <LabelList dataKey="count" position="right" style={{ fill: ct.tick, fontSize: 11 }} />
                {lostReasons.map((_, i) => <Cell key={i} fill={LOSS_COLORS[i % LOSS_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
        {lostReasons.length > 0 && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3" data-testid="analytics-lost-legend">
            {lostReasons.map((r, i) => (
              <button key={r.reason} onClick={() => setOpenBucket(openBucket === r.reason ? null : r.reason)} data-testid={`lost-reason-drill-${i}`}
                className={`inline-flex items-center gap-1.5 text-[11px] transition-colors ${openBucket === r.reason ? "text-slate-900 font-semibold" : "text-slate-500 hover:text-slate-800"}`}>
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: LOSS_COLORS[i % LOSS_COLORS.length] }} />
                {r.reason} <span className="text-slate-400">· {r.count}</span>
              </button>
            ))}
          </div>
        )}
        {openBucket && (() => { const r = lostReasons.find((x) => x.reason === openBucket); return r ? (
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="analytics-lost-bucket-detail">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold text-slate-700">{r.reason} — what the team wrote ({r.count} bid{r.count === 1 ? "" : "s"})</div>
              <button onClick={() => pickReason("lost", r.reason)} data-testid="analytics-lost-bucket-table" className="text-[11px] text-[#003E87] hover:underline">Show these bids in the table</button>
            </div>
            <ul className="mt-2 space-y-1">
              {(r.details || []).map((d) => <li key={d.text} className="text-[11px] text-slate-600 flex justify-between gap-4"><span>{d.text}</span><span className="text-slate-400 tabular-nums shrink-0">{d.count}</span></li>)}
              {!(r.details || []).length && <li className="text-[11px] text-slate-400">No free-text notes behind this bucket.</li>}
            </ul>
          </div>
        ) : null; })()}
        {lostReasons.length > 0 && <p className="text-[11px] text-slate-400 mt-2">Click a bar to drill the Bid History table into those lost bids; click a legend bucket to read what the team wrote.</p>}
      </Card>

      {/* Why we win — win-reason breakdown */}
      <Card className="p-5" data-testid="analytics-win-reasons">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">Why we win — won bids by reason</h2>
          <span className="text-xs text-slate-400">{num(a.win_count || 0)} won pursuit{(a.win_count || 0) === 1 ? "" : "s"}</span>
        </div>
        {winReasons.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-400" data-testid="analytics-win-empty">No win reasons captured yet. Mark a pursuit Won and pick a reason to see what's landing deals.</div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={Math.max(180, winReasons.length * 46)} minHeight={180}>
              <BarChart data={winReasons} layout="vertical" margin={{ left: 10, right: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={ct.grid} horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: ct.tick }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="reason" width={168} interval={0} tickFormatter={(v) => (v && v.length > 22 ? v.slice(0, 22) + "…" : v)} tick={{ fontSize: 11, fill: ct.tick }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v, n, p) => [`${v} bid${v === 1 ? "" : "s"} · ${moneyCompact(p.payload.value)}`, "Won"]}
                  contentStyle={ct.contentStyle} itemStyle={ct.itemStyle} labelStyle={ct.labelStyle} cursor={{ fill: ct.cursor }} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]} className="cursor-pointer" onClick={(d) => pickReason("won", d?.reason || d?.payload?.reason)}>
                  <LabelList dataKey="count" position="right" style={{ fill: ct.tick, fontSize: 11 }} />
                  {winReasons.map((_, i) => <Cell key={i} fill={WIN_COLORS[i % WIN_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3" data-testid="analytics-win-legend">
              {winReasons.map((r, i) => (
                <button key={r.reason} onClick={() => pickReason("won", r.reason)} data-testid={`win-reason-drill-${i}`}
                  className="inline-flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-slate-800 transition-colors">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: WIN_COLORS[i % WIN_COLORS.length] }} />
                  {r.reason} <span className="text-slate-400">· {r.count}</span>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Tip: click a bar or legend to drill the Bid History table into those won bids.</p>
          </>
        )}
      </Card>

      {/* Wins over time */}
      {wonTrend.length > 0 && (
        <Card className="p-5" data-testid="analytics-won-trend">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">Wins over time</h2>
            <span className="text-xs text-slate-400">{num(a.won_count || 0)} won · {moneyCompact(a.won_value || 0)}</span>
          </div>
          <ResponsiveContainer width="100%" height={300} minHeight={260}>
            <BarChart data={wonTrend} margin={{ left: 10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={ct.grid} vertical={false} />
              <XAxis dataKey="period" tick={{ fontSize: 11, fill: ct.tick }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: ct.tick }} axisLine={false} tickLine={false} width={44}
                tickFormatter={(v) => moneyCompact(v)} />
              <Tooltip contentStyle={ct.contentStyle} itemStyle={ct.itemStyle} labelStyle={ct.labelStyle} cursor={{ fill: ct.cursor }}
                formatter={(v, n, p) => [`${moneyCompact(v)} · ${p.payload.count} won`, "Won value"]} />
              <Bar dataKey="value" fill="#10B981" radius={[4, 4, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Loss reasons over time */}
      {lostTrend.length > 0 && reasonKeys.length > 0 && (
        <Card className="p-5" data-testid="analytics-lost-trend">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 mb-4">Loss reasons over time</h2>
          <ResponsiveContainer width="100%" height={300} minHeight={260}>
            <BarChart data={lostTrend} margin={{ left: 10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={ct.grid} vertical={false} />
              <XAxis dataKey="period" tick={{ fontSize: 11, fill: ct.tick }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: ct.tick }} axisLine={false} tickLine={false} width={32} />
              <Tooltip contentStyle={ct.contentStyle} itemStyle={ct.itemStyle} labelStyle={ct.labelStyle} cursor={{ fill: ct.cursor }} />
              {reasonKeys.map((k, i) => (
                <Bar key={k} dataKey={k} stackId="loss" fill={LOSS_COLORS[i % LOSS_COLORS.length]}
                  radius={i === reasonKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
            {reasonKeys.map((k, i) => (
              <span key={k} className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: LOSS_COLORS[i % LOSS_COLORS.length] }} />
                {k}
              </span>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-5" data-testid="analytics-chart-worth-bidding">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 mb-4">Who is worth bidding</h2>
        <div className="space-y-2.5">
          {a.worth_bidding.map((w) => (
            <div key={w.client} className="flex items-center gap-3">
              <div className="w-40 text-sm text-slate-700 truncate">{w.client}</div>
              <div className="flex-1 h-6 bg-slate-100 rounded overflow-hidden relative">
                <div className="h-full bg-emerald-500" style={{ width: `${w.hit_rate}%` }} />
                <span className="absolute inset-y-0 left-2 flex items-center text-[11px] font-mono text-slate-700">
                  {w.hit_rate}% · {w.won}/{w.count}
                </span>
              </div>
              <div className="w-16 text-right text-xs font-mono text-slate-500">{moneyCompact(w.won_value)}</div>
            </div>
          ))}
        </div>
      </Card>

      {records.length > 0 && (
        <Card className="p-5" data-testid="analytics-bid-history" ref={tableRef}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">Bid history — {filtered.length} of {records.length} record{records.length === 1 ? "" : "s"}</h2>
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={filtered.length === 0} data-testid="bid-history-export-csv">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search project, client, state, loss reason…" className="h-9 pl-8" data-testid="bid-history-search" />
            </div>
            <select value={outcome} onChange={(e) => setOutcome(e.target.value)} data-testid="bid-history-filter-outcome"
              className="h-9 text-sm border border-slate-200 rounded-md px-2 bg-white text-slate-700">
              <option value="all">All outcomes</option>
              <option value="won">Won</option>
              <option value="lost">Lost</option>
            </select>
            <select value={client} onChange={(e) => setClient(e.target.value)} data-testid="bid-history-filter-client"
              className="h-9 text-sm border border-slate-200 rounded-md px-2 bg-white text-slate-700 max-w-[180px]">
              <option value="all">All clients</option>
              {clients.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={state} onChange={(e) => setState(e.target.value)} data-testid="bid-history-filter-state"
              className="h-9 text-sm border border-slate-200 rounded-md px-2 bg-white text-slate-700">
              <option value="all">All states</option>
              {states.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {reasonFilter && (
              <span className={`inline-flex items-center gap-1 text-xs border rounded-full px-2 py-1 ${reasonFilter.outcome === "won" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`} data-testid="bid-history-reason-chip">
                {reasonFilter.outcome === "won" ? "Won" : "Lost"}: {reasonFilter.reason}
                <button onClick={() => setReasonFilter(null)} className="hover:opacity-70" data-testid="bid-history-reason-clear"><X className="w-3 h-3" /></button>
              </span>
            )}
            {anyFilter && (
              <button onClick={clearFilters} data-testid="bid-history-clear-filters" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800">
                <X className="w-3.5 h-3.5" /> Clear
              </button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                  <th className="text-left font-medium px-2 py-2">Project</th>
                  <th className="text-left font-medium px-2 py-2">Client</th>
                  <th className="text-left font-medium px-2 py-2">State</th>
                  <th className="text-right font-medium px-2 py-2">MW</th>
                  <th className="text-right font-medium px-2 py-2">$/W</th>
                  <th className="text-right font-medium px-2 py-2">Total</th>
                  <th className="text-left font-medium px-2 py-2 pl-4">Outcome</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.bid_id} onClick={() => navigate(`/bids/${r.bid_id}`)} data-testid={`bid-history-row-${r.bid_id}`}
                    className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors">
                    <td className="px-2 py-2 font-medium text-slate-800">{r.name}{r.loss_reason && r.outcome === "lost" ? <span className="block text-[11px] font-normal text-rose-500">{r.loss_reason}</span> : null}</td>
                    <td className="px-2 py-2 text-slate-500">{r.client || "—"}</td>
                    <td className="px-2 py-2 text-slate-500">{r.state || "—"}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-600">{r.mw ? num(r.mw) : "—"}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-600">{r.ppw ? `$${Number(r.ppw).toFixed(3)}` : "—"}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums font-semibold text-slate-900">{moneyCompact(r.total)}</td>
                    <td className="px-2 py-2 pl-4"><span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${r.outcome === "won" ? "bg-emerald-50 text-emerald-700" : r.outcome === "lost" ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-500"}`}>{r.outcome}</span></td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="px-2 py-8 text-center text-sm text-slate-400" data-testid="bid-history-no-match">No records match your filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
